/**
 * ExportQueue · Complete Priority 2 export UI.
 *
 * • Generate Complete GIS Map Package PDF
 * • Generate Continuous Drillhole Log Book PDF (with vertical scale + hole selector)
 * • Persistent queue: waiting · generating · ready · failed with retry/download
 *
 * Downloads work independently — no CPR generation required.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  DownloadSimple, CircleNotch, CheckCircle, XCircle, ArrowClockwise,
  FilePdf, Files, Info, Warning, ClockCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const STATUS_BADGE = {
  Queued:       { tone: "slate",   Icon: ClockCounterClockwise, label: "Waiting" },
  "In Progress": { tone: "blue",    Icon: CircleNotch,             label: "Generating" },
  Completed:    { tone: "emerald", Icon: CheckCircle,             label: "Ready" },
  Failed:        { tone: "rose",    Icon: XCircle,                  label: "Failed" },
};
const TONE_CLS = {
  slate:    "border-slate-200 bg-slate-50 text-slate-700",
  blue:     "border-brand-blue/30 bg-brand-blue/5 text-brand-blue",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
  rose:     "border-rose-200 bg-rose-50 text-rose-800",
};

export default function ExportQueue({ pid }) {
  const [verticalScale, setVerticalScale] = useState(100);
  const [holeIds,       setHoleIds]       = useState("");

  const jobsQ = useQuery({
    queryKey: ["export-jobs", pid],
    queryFn: () => api.get(`/exports/jobs?project_id=${pid}`).then((r) => r.data).catch(() => ({ jobs: [] })),
    refetchInterval: 3500,
  });
  // Fallback: try listing latest per kind
  const mapLatestQ = useQuery({
    queryKey: ["latest-map", pid],
    queryFn: () => api.get(`/projects/${pid}/exports/latest?kind=gis_map_book`).then((r) => r.data).catch(() => null),
    refetchInterval: 15_000,
  });
  const logLatestQ = useQuery({
    queryKey: ["latest-log", pid],
    queryFn: () => api.get(`/projects/${pid}/exports/latest?kind=borehole_log_book`).then((r) => r.data).catch(() => null),
    refetchInterval: 15_000,
  });

  const enqueueMap = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/gis/map-book/generate`).then((r) => r.data),
    onSuccess: (r) => { toast.success("GIS map book queued"); jobsQ.refetch(); pollJob(r.job_id); },
    onError:   (e) => toast.error(e?.response?.data?.detail || "Failed to enqueue"),
  });
  const enqueueLog = useMutation({
    mutationFn: () => {
      const params = new URLSearchParams();
      params.set("vertical_scale", String(verticalScale));
      const cleaned = holeIds.trim();
      if (cleaned) params.set("hole_ids", cleaned);
      return api.post(`/projects/${pid}/boreholes/log-book/generate?${params.toString()}`).then((r) => r.data);
    },
    onSuccess: (r) => { toast.success("Log book queued"); jobsQ.refetch(); pollJob(r.job_id); },
    onError:   (e) => toast.error(e?.response?.data?.detail || "Failed to enqueue"),
  });

  const [polledJobs, setPolledJobs] = useState({});   // {jobId: doc}
  async function pollJob(jobId) {
    for (let i = 0; i < 90; i++) {
      try {
        const r = await api.get(`/projects/${pid}/exports/jobs/${jobId}/status`);
        setPolledJobs((p) => ({ ...p, [jobId]: r.data }));
        if (["Completed", "Failed"].includes(r.data.job_status)) break;
      } catch { /* silent */ }
      await new Promise((res) => setTimeout(res, 2500));
    }
    jobsQ.refetch(); mapLatestQ.refetch(); logLatestQ.refetch();
  }

  const retryMut = useMutation({
    mutationFn: (jid) => api.post(`/projects/${pid}/exports/jobs/${jid}/retry`).then((r) => r.data),
    onSuccess: (r) => { toast.success("Job requeued"); pollJob(r.job_id); },
  });

  const download = (reportId, filename) => {
    const url = api.defaults.baseURL + `/projects/${pid}/exports/${reportId}/download`;
    const a = document.createElement("a");
    a.href = url; a.setAttribute("download", filename || "export.pdf");
    a.click();
  };

  const queueJobs = useMemo(() => {
    // merge polled + fetched
    const seen = new Map();
    (jobsQ.data?.jobs || []).forEach((j) => seen.set(j.id, j));
    Object.values(polledJobs).forEach((j) => { if (j.id) seen.set(j.id, j); });
    return Array.from(seen.values()).sort((a, b) =>
      String(b.started_at || "").localeCompare(String(a.started_at || "")));
  }, [jobsQ.data, polledJobs]);

  return (
    <div className="space-y-4" data-testid="export-queue-panel">
      {/* Generation controls */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="gen-map-book-card">
          <div className="flex items-center gap-3 mb-2">
            <FilePdf size={16} className="text-brand-blue" weight="duotone" />
            <div className="flex-1"><h2 className="font-heading font-bold text-[14px] text-slate-900">Complete GIS Map Package</h2>
              <div className="text-[11.5px] text-slate-500">Every ready GIS map, one per landscape page. Includes cover, TOC and per-map AI interpretation captions.</div>
            </div>
          </div>
          <Button size="sm" onClick={() => enqueueMap.mutate()} disabled={enqueueMap.isPending}
                     data-testid="btn-generate-map-book"
                     className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px] w-full gap-1.5">
            {enqueueMap.isPending ? <CircleNotch size={12} className="animate-spin" /> : <DownloadSimple size={12} weight="fill" />}
            Generate GIS Map Package
          </Button>
          {mapLatestQ.data && (
            <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50/60 p-2 text-[11px] flex items-center gap-2">
              <CheckCircle size={11} className="text-emerald-600" weight="fill" />
              <div className="flex-1 min-w-0">
                <div className="font-mono truncate">{mapLatestQ.data.filename}</div>
                <div className="text-slate-500 text-[10px]">
                  {mapLatestQ.data.map_count || "?"} maps · {Math.round((mapLatestQ.data.file_size_bytes || 0) / 1024)} KB · {new Date(mapLatestQ.data.created_at).toLocaleString()}
                </div>
              </div>
              <button type="button" onClick={() => download(mapLatestQ.data.id, mapLatestQ.data.filename)}
                          data-testid="btn-download-latest-map"
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 text-[10.5px] font-medium">
                <DownloadSimple size={10} weight="fill" /> Download
              </button>
            </div>
          )}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="gen-log-book-card">
          <div className="flex items-center gap-3 mb-2">
            <FilePdf size={16} className="text-brand-blue" weight="duotone" />
            <div className="flex-1"><h2 className="font-heading font-bold text-[14px] text-slate-900">Continuous Drillhole Log Book</h2>
              <div className="text-[11.5px] text-slate-500">Multi-page continuous log per hole with configurable vertical scale.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <label className="block">
              <div className="text-[9.5px] uppercase tracking-widest text-slate-500 mb-0.5">Vertical scale</div>
              <select value={verticalScale} onChange={(e) => setVerticalScale(Number(e.target.value))}
                        data-testid="select-vertical-scale"
                        className="w-full h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px]">
                <option value={50}>50 m per page</option>
                <option value={100}>100 m per page</option>
                <option value={200}>200 m per page</option>
                <option value={250}>250 m per page</option>
                <option value={500}>500 m per page</option>
              </select>
            </label>
            <label className="block">
              <div className="text-[9.5px] uppercase tracking-widest text-slate-500 mb-0.5">Hole IDs (optional, comma-sep)</div>
              <input type="text" value={holeIds} onChange={(e) => setHoleIds(e.target.value)}
                       placeholder="Blank = all"
                       data-testid="input-hole-ids"
                       className="w-full h-8 rounded-md border border-slate-200 bg-white px-2 text-[12px] font-mono" />
            </label>
          </div>
          <Button size="sm" onClick={() => enqueueLog.mutate()} disabled={enqueueLog.isPending}
                     data-testid="btn-generate-log-book"
                     className="bg-brand-blue hover:bg-brand-blue/90 text-white text-[11.5px] w-full gap-1.5">
            {enqueueLog.isPending ? <CircleNotch size={12} className="animate-spin" /> : <DownloadSimple size={12} weight="fill" />}
            Generate Log Book
          </Button>
          {logLatestQ.data && (
            <div className="mt-3 rounded-md border border-emerald-200 bg-emerald-50/60 p-2 text-[11px] flex items-center gap-2">
              <CheckCircle size={11} className="text-emerald-600" weight="fill" />
              <div className="flex-1 min-w-0">
                <div className="font-mono truncate">{logLatestQ.data.filename}</div>
                <div className="text-slate-500 text-[10px]">
                  {logLatestQ.data.hole_count || "?"} holes · {Math.round((logLatestQ.data.file_size_bytes || 0) / 1024)} KB · {new Date(logLatestQ.data.created_at).toLocaleString()}
                </div>
              </div>
              <button type="button" onClick={() => download(logLatestQ.data.id, logLatestQ.data.filename)}
                          data-testid="btn-download-latest-log"
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 text-[10.5px] font-medium">
                <DownloadSimple size={10} weight="fill" /> Download
              </button>
            </div>
          )}
        </section>
      </div>

      {/* Persistent queue */}
      <section className="rounded-lg border border-slate-200 bg-white p-5" data-testid="generation-queue">
        <div className="flex items-center gap-3 mb-3">
          <ClockCounterClockwise size={16} className="text-slate-500" />
          <h2 className="font-heading font-bold text-[14px] text-slate-900">Generation Queue</h2>
          <button type="button" onClick={() => jobsQ.refetch()}
                     data-testid="btn-refresh-queue"
                     className="ml-auto text-[11px] text-slate-500 hover:text-brand-blue">
            <ArrowClockwise size={11} className={jobsQ.isFetching ? "animate-spin" : ""} />
          </button>
        </div>
        {queueJobs.length === 0 && (
          <div className="text-[11.5px] text-slate-500 italic">No queued or completed jobs yet.</div>
        )}
        <div className="space-y-2">
          {queueJobs.slice(0, 30).map((j) => {
            const meta = STATUS_BADGE[j.job_status] || STATUS_BADGE.Queued;
            return (
              <div key={j.id} data-testid={`queue-row-${j.id}`}
                      className="grid grid-cols-[24px_140px_1fr_120px_60px_auto] items-center gap-3 rounded-md border border-slate-200 bg-slate-50/40 px-3 py-2 text-[11px]">
                <meta.Icon size={13} className={j.job_status === "In Progress" ? "text-brand-blue animate-spin" : "text-slate-500"} weight="fill" />
                <span className={`inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[9.5px] uppercase tracking-widest ${TONE_CLS[meta.tone]}`}>
                  {meta.label}
                </span>
                <div className="min-w-0">
                  <div className="text-slate-800 font-medium">{(j.kind || "").replace("_", " ")}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {j.current_stage || j.job_status} · {j.progress || 0}%
                    {j.error_message && <span className="text-rose-700 ml-2">· {j.error_message}</span>}
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                  <div className="h-full bg-brand-blue transition-all"
                       style={{ width: `${Math.max(0, Math.min(100, j.progress || 0))}%` }} />
                </div>
                <div className="text-[10px] text-slate-500 font-mono">{new Date(j.started_at || Date.now()).toLocaleTimeString()}</div>
                <div className="flex items-center gap-1 justify-end">
                  {j.job_status === "Completed" && j.report_id && (
                    <button type="button" onClick={() => download(j.report_id, j.filename)}
                                data-testid={`btn-download-${j.id}`}
                                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 text-[10.5px]">
                      <DownloadSimple size={10} weight="fill" /> Download
                    </button>
                  )}
                  {j.job_status === "Failed" && (
                    <button type="button" onClick={() => retryMut.mutate(j.id)}
                                data-testid={`btn-retry-${j.id}`}
                                className="inline-flex items-center gap-1 rounded-md bg-rose-600 hover:bg-rose-700 text-white px-2 py-1 text-[10.5px]">
                      <ArrowClockwise size={10} /> Retry
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="rounded-md border border-slate-200 bg-slate-50/70 px-3 py-2 text-[11px] text-slate-600 flex items-start gap-2">
        <Info size={11} className="text-slate-400 mt-0.5" />
        <div>
          These downloads work independently. You do <b>not</b> need to run a CPR job to get the GIS Map Package or the Continuous Drillhole Log Book.
        </div>
      </div>
    </div>
  );
}
